import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Check, X, MessageSquareText, Clock, User as UserIcon, BookOpen } from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Teacher-side Schedule Requests page — pending list + history.
 * Auto-refreshes every 30s. Includes an inline **Suggest slots** picker
 * that lets the teacher counter-propose from their own availability.
 */
export default function ScheduleRequestsPanel() {
  const [reqs, setReqs] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [suggestOpenFor, setSuggestOpenFor] = useState(null);   // request.id
  const [suggestPicks, setSuggestPicks] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        api.get('/scheduler/requests/mine'),
        api.get('/scheduler/availability/me'),
      ]);
      setReqs(r.data.requests || []);
      setAvailability(a.data.slots || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const decide = async (id, action, reason = '', suggested_slots = null) => {
    setBusy(id);
    try {
      const payload = { action, reason };
      if (suggested_slots) payload.suggested_slots = suggested_slots;
      await api.post(`/scheduler/requests/${id}/decide`, payload);
      toast.success(
        action === 'approve' ? 'Approved · Classes scheduled' :
        action === 'reject' ? 'Rejected' :
        'Alternative slots suggested'
      );
      setSuggestOpenFor(null); setSuggestPicks([]);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };

  const openSuggest = (req) => {
    setSuggestOpenFor(req.id);
    setSuggestPicks([]);
  };
  const toggleSuggestPick = (slot) => {
    const key = slot.kind === 'recurring' ? `r:${slot.day_of_week}:${slot.start_time}` : `o:${slot.date}:${slot.start_time}`;
    const already = suggestPicks.some(p => (p.kind === 'recurring' ? `r:${p.day_of_week}:${p.start_time}` : `o:${p.date}:${p.start_time}`) === key);
    if (already) {
      setSuggestPicks(suggestPicks.filter(p => (p.kind === 'recurring' ? `r:${p.day_of_week}:${p.start_time}` : `o:${p.date}:${p.start_time}`) !== key));
    } else {
      setSuggestPicks([...suggestPicks, slot]);
    }
  };
  const submitSuggest = (req) => {
    if (suggestPicks.length === 0) { toast.error('Pick at least one alternative slot'); return; }
    // Match req.schedule_type to correct kind
    const wantedKind = req.schedule_type === 'long_term' ? 'recurring' : 'onetime';
    const badPick = suggestPicks.find(p => p.kind !== wantedKind);
    if (badPick) { toast.error(`Only ${wantedKind} slots are valid for this request`); return; }
    const slots = suggestPicks.map(p => p.kind === 'recurring'
      ? { day_of_week: p.day_of_week, start_time: p.start_time }
      : { date: p.date, start_time: p.start_time });
    decide(req.id, 'suggest', '', slots);
  };

  const pending = reqs.filter(r => r.status === 'pending');
  const decided = reqs.filter(r => r.status !== 'pending');

  return (
    <div className="space-y-6" data-testid="schedule-requests-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-[#0F172A]">Schedule Requests</h2>
        <span className="text-xs text-slate-500">Auto-refreshes every 30s</span>
      </div>

      {/* Pending */}
      <div>
        <h3 className="text-sm font-bold text-amber-700 mb-2" data-testid="pending-heading">Pending Approval ({pending.length})</h3>
        {loading ? <p className="text-sm text-slate-400">Loading…</p> : pending.length === 0 ? (
          <p className="text-sm text-slate-400">No pending requests.</p>
        ) : (
          <div className="space-y-3">
            {pending.map(r => {
              const wantedKind = r.schedule_type === 'long_term' ? 'recurring' : 'onetime';
              // For suggest picker, offer teacher's own availability matching the request kind
              // that isn't already the requested slot(s).
              const isRequestedSlot = (s) => (r.slots || []).some(rs =>
                wantedKind === 'recurring'
                  ? rs.day_of_week === s.day_of_week && rs.start_time === s.start_time
                  : rs.date === s.date && rs.start_time === s.start_time
              );
              const suggestableSlots = availability.filter(s => s.kind === wantedKind && !isRequestedSlot(s) && !s.reserved);
              return (
              <div key={r.id} className="nb-card p-4 border-l-4 border-l-amber-400" data-testid={`req-row-${r.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-bold text-slate-900 flex items-center gap-1.5"><UserIcon className="w-4 h-4 text-indigo-600" /> {r.student_name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {r.vertical}</span>
                      <span className="capitalize font-semibold text-slate-700">· {r.schedule_type.replace('_', ' ')}</span>
                    </p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold">PENDING</span>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 mb-3">
                  <p className="text-[10px] font-bold text-slate-500 mb-1 uppercase">Requested Slots</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(r.slots || []).map((s, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-800 font-bold">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {s.day_of_week != null ? DAYS[s.day_of_week] : s.date} · {s.start_time}
                      </span>
                    ))}
                  </div>
                  {r.suggested_slots && r.suggested_slots.length > 0 && (
                    <>
                      <p className="text-[10px] font-bold text-amber-700 mt-2 mb-1 uppercase">You Suggested</p>
                      <div className="flex flex-wrap gap-1.5">
                        {r.suggested_slots.map((s, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 font-bold">
                            {s.day_of_week != null ? DAYS[s.day_of_week] : s.date} · {s.start_time}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {suggestOpenFor === r.id ? (
                  <div className="space-y-2 p-3 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50" data-testid={`suggest-panel-${r.id}`}>
                    <p className="text-xs font-bold text-amber-800">Pick alternative {wantedKind === 'recurring' ? 'recurring' : 'one-time'} slot(s) from your availability:</p>
                    {suggestableSlots.length === 0 ? (
                      <p className="text-xs text-slate-500">No other {wantedKind} slots available. Add more in the Availability tab.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                        {suggestableSlots.map(s => {
                          const key = s.kind === 'recurring' ? `r:${s.day_of_week}:${s.start_time}` : `o:${s.date}:${s.start_time}`;
                          const picked = suggestPicks.some(p => (p.kind === 'recurring' ? `r:${p.day_of_week}:${p.start_time}` : `o:${p.date}:${p.start_time}`) === key);
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => toggleSuggestPick(s)}
                              className={`text-left text-xs px-2 py-1.5 rounded-md border ${picked ? 'bg-amber-500 text-white border-amber-500' : 'bg-white border-slate-200 hover:border-amber-400'}`}
                              data-testid={`suggest-slot-${key}`}
                            >
                              {s.kind === 'recurring' ? `Every ${DAYS[s.day_of_week]}` : s.date} · <b>{s.start_time}</b>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => submitSuggest(r)} disabled={busy === r.id || suggestPicks.length === 0} className="bg-amber-600 hover:bg-amber-700 text-xs" data-testid={`send-suggest-${r.id}`}>
                        <MessageSquareText className="w-3 h-3 mr-1" /> Send Suggestion
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setSuggestOpenFor(null); setSuggestPicks([]); }} className="text-xs">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => decide(r.id, 'approve')} disabled={busy === r.id} className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid={`approve-${r.id}`}>
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" onClick={() => openSuggest(r)} disabled={busy === r.id} className="bg-amber-500 hover:bg-amber-600 text-white text-xs" data-testid={`open-suggest-${r.id}`}>
                      <MessageSquareText className="w-3 h-3 mr-1" /> Suggest Slot
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decide(r.id, 'reject', window.prompt('Reason (optional):') || '')} disabled={busy === r.id} className="border-red-500 text-red-700 hover:bg-red-50 text-xs" data-testid={`reject-${r.id}`}>
                      <X className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* History */}
      {decided.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-600 mb-2">History ({decided.length})</h3>
          <div className="space-y-2">
            {decided.slice(0, 15).map(r => (
              <div key={r.id} className={`nb-card p-3 border-l-4 ${r.status === 'approved' ? 'border-l-emerald-500' : r.status === 'rejected' ? 'border-l-red-400' : 'border-l-slate-300'}`} data-testid={`hist-row-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-slate-900">{r.student_name}</p>
                    <p className="text-[11px] text-slate-500">{r.vertical} · {r.schedule_type.replace('_', ' ')} · {(r.slots || []).length} slot(s)</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                    {r.status.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
